import { StateCreator } from "zustand";
import { VendState } from ".";
import { Address, Location, LocationStatusImpact } from "../types";
import {
  fetchLocations,
  createLocation as createLocationApi,
  editLocation as editLocationApi,
  editLocationAddress,
  activateLocation as activateLocationApi,
  putLocationOnHold as putLocationOnHoldApi,
} from "../api/location/api";
import { fetchMachines } from "../api/machine/api";
import { addLocationContact, listLocationContacts } from "../api/location/contacts";
import { inviteLocationContact } from "../api/user/api";
import { deriveAvailable } from "./machine";
import { hasRequiredContactFields } from "../contactUtils";
import { normalizeIdentityEmail } from "../../utils";
import { useRoutePlannerStore } from "./routePlanner";
import { useUserStore } from "../../user/state/store";
import { runLoad } from "./helpers";
import { withWriteAction } from "../utils";
import { machineTypeLabel } from "../utils";
import { taskScopeKeys } from "../taskScopes";
import { resetMachineCollectionSummaryCache } from "../liveSales/store";

const deriveAddresses = (locations: Location[] = []): Address[] =>
  locations
    .map((loc) => loc.address)
    .filter((addr): addr is Address => Boolean(addr));

const replaceLocation = (locations: Location[], updated: Location) => {
  if (!updated.id) return locations;
  let found = false;
  const next = locations.map((location) => {
    if (location.id !== updated.id) return location;
    found = true;
    return updated;
  });
  return found ? next : [...next, updated];
};

let locationsPromise: Promise<void> | null = null;
let locationsLoadGeneration = 0;

export const resetLocationLoadState = () => {
  locationsPromise = null;
  locationsLoadGeneration += 1;
};

export type LocationSlice = {
  locations: Location[];
  addresses: Address[];
  locationsLoading: boolean;
  locationsError: string | null;
  locationsHydrated: boolean;
  locationsFetchedAt: number | null;
  setLocations: (locations: Location[]) => void;
  addLocation: (location: Location) => void;
  loadLocations: (options?: { force?: boolean }) => Promise<void>;
  createLocation: (location: Location) => Promise<Location>;
  editLocation: (
    location: Location,
    options?: { skipTaskRefresh?: boolean }
  ) => Promise<Location>;
  activateLocation: (locationId: string) => Promise<Location>;
  putLocationOnHold: (locationId: string, impact: LocationStatusImpact) => Promise<Location>;
};

export const createLocationSlice: StateCreator<
  VendState,
  [],
  [],
  LocationSlice
> = (set, get, _store) => ({
  locations: [],
  addresses: [],
  locationsLoading: false,
  locationsError: null,
  locationsHydrated: false,
  locationsFetchedAt: null,
  setLocations: (locations) =>
    set({
      locations,
      addresses: deriveAddresses(locations),
      locationsFetchedAt: Date.now(),
    } as VendState),
  addLocation: (location) => {
    const state = get();
    set({
      locations: [...state.locations, location],
      locationsFetchedAt: Date.now(),
    } as VendState);
  },
  loadLocations: async (options) => {
    const state = get() as VendState;
    if (!options?.force && state.locationsHydrated) return;
    if (!options?.force && locationsPromise) return locationsPromise;
    const authUserIdAtStart = useUserStore.getState().id || "";
    const generationAtStart = locationsLoadGeneration;
    const request = (async () => {
      try {
        await runLoad({
          isLoading: () => (get() as VendState).locationsLoading,
          setLoading: (loading) =>
            set((current) => ({
              ...(current as VendState),
              locationsLoading: loading,
            })),
          fetcher: fetchLocations,
          setError: (message) => {
            if (locationsLoadGeneration === generationAtStart && (useUserStore.getState().id || "") === authUserIdAtStart) {
              set({ locationsError: message } as VendState);
            }
          },
          onSuccess: (data) => {
            const currentUserId = useUserStore.getState().id || "";
            if (
              locationsLoadGeneration !== generationAtStart ||
              currentUserId !== authUserIdAtStart
            ) {
              return;
            }
            set({
              locations: data,
              addresses: deriveAddresses(data),
              locationsFetchedAt: Date.now(),
            } as VendState);
          },
        });
      } finally {
        const currentUserId = useUserStore.getState().id || "";
        if (
          locationsLoadGeneration === generationAtStart &&
          currentUserId === authUserIdAtStart
        ) {
          set({ locationsHydrated: true } as VendState);
        }
        if (!options?.force) {
          locationsPromise = null;
        }
      }
    })();
    if (!options?.force) {
      locationsPromise = request;
    }
    return request;
  },
  createLocation: async (location) => {
    return await withWriteAction(
      "Creating Location",
      async () => {
      const created = await createLocationApi(location);
      const contactDrafts = (location.contacts || []).filter(
        hasRequiredContactFields
      );
      if (created.id && contactDrafts.length) {
        try {
          const outcomes = await Promise.allSettled(
            contactDrafts.map(async (contact) => {
              const payload = {
                firstName: (contact.firstName || "").trim(),
                lastName: (contact.lastName || "").trim(),
                email: normalizeIdentityEmail(contact.email),
                phone: contact.phone || null,
                role: (contact.role as any) || "staff",
              };
              await addLocationContact({
                locationId: created.id || "",
                contact: payload,
              });
              await inviteLocationContact({
                locationId: created.id || "",
                ...payload,
              });
            })
          );
          if (outcomes.some(outcome => outcome.status === "rejected")) {
            created.creationWarnings = ["Some contacts or invitations could not be saved. Open the location to review contacts and resend invitations."];
          }
          const refreshed = await listLocationContacts(created.id);
          created.contacts = refreshed;
        } catch {
          created.creationWarnings = ["The location was created, but its contacts could not be confirmed. Open the location to review contacts and invitations."];
        }
      }
      const state = get();
      const nextLocations = replaceLocation(
        (get() as VendState).locations,
        created
      );
      set({
        locations: nextLocations,
        addresses: deriveAddresses(nextLocations),
        locationsFetchedAt: Date.now(),
      } as VendState);
      if ((created.machines || []).length > 0) {
        try {
          const machines = (await fetchMachines()).map((m) => ({
            ...m,
            type: machineTypeLabel(m.type),
          }));
          state.setMachines?.(machines);
          state.setAvailableMachines?.(deriveAvailable(machines));
        } catch {
          // Ignore machine refresh errors.
        }
      }
      if (typeof state.loadTasks === "function") {
        state.loadTasks({ force: true }).catch(() => {});
      }

      return created;
      }
    );
  },
  editLocation: async (location, options) => {
    return await withWriteAction("Saving Location", async () => {
      const state = get();
      const current = state.locations.find((l) => l.id === location.id);
      if (!current) {
        throw new Error("Location not found");
      }

      const updatedPayload: Location = {
        ...current,
        ...(location as Location),
      };

      const addressChanged =
        updatedPayload.address &&
        JSON.stringify(updatedPayload.address) !==
          JSON.stringify(current.address);
      if (addressChanged && updatedPayload.id && updatedPayload.address) {
        await editLocationAddress(
          updatedPayload.id,
          updatedPayload.address as Address
        );
      }

      const saved = await editLocationApi(updatedPayload);
      const nextLocations = replaceLocation(
        (get() as VendState).locations,
        saved
      );
      set({
        locations: nextLocations,
        addresses: deriveAddresses(nextLocations),
        locationsFetchedAt: Date.now(),
      } as VendState);
      if (addressChanged) {
        resetMachineCollectionSummaryCache(
          Object.keys(saved.machineAssignmentInfo || updatedPayload.machineAssignmentInfo || {})
        );
      }
      if (!options?.skipTaskRefresh && saved.id) {
        await state.loadTasks?.({
          force: true,
          silent: true,
          filters: { locationId: saved.id },
          scopeKey: taskScopeKeys.location(saved.id),
        });
      } else if (!options?.skipTaskRefresh) {
        await state.loadTasks?.({ force: true });
      }
      useRoutePlannerStore.getState().reset();
      return saved || updatedPayload;
    });
  },
  activateLocation: async (locationId) => {
    return withWriteAction("Reactivating Location", async () => {
      const updated = await activateLocationApi(locationId);
      const locations = replaceLocation((get() as VendState).locations, updated);
      set({ locations, addresses: deriveAddresses(locations), locationsFetchedAt: Date.now() } as VendState);
      return updated;
    });
  },
  putLocationOnHold: async (locationId, impact) => {
    return withWriteAction("Putting Location on Hold", async () => {
      const machineIds = new Set(impact.assignedMachines.map((machine) => machine.id));
      const updated = await putLocationOnHoldApi(
        locationId,
        machineIds.size > 0,
        Array.from(machineIds),
      );
      const state = get() as VendState;
      const locations = replaceLocation(state.locations, updated);
      const machines = state.machines.map((machine) =>
        machine.id && machineIds.has(machine.id)
          ? { ...machine, assigned: false, location: undefined }
          : machine,
      );
      set({
        locations,
        addresses: deriveAddresses(locations),
        locationsFetchedAt: Date.now(),
        machines,
        availableMachines: deriveAvailable(machines),
      } as VendState);
      state.removeTaskSnapshots?.(impact.tasksToDelete.map((task) => task.id));
      useRoutePlannerStore.getState().reset();
      return updated;
    });
  },
});
